import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: transparent;
`;

const MenuContainer = styled.div<{ x: number; y: number }>`
  position: fixed;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
  z-index: 1001;
  min-width: 200px;
  overflow: hidden;
  animation: slideIn 0.15s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  text-align: left;
  font-size: 0.875rem;
  color: #2d3748;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #f7fafc;
    color: #667eea;
  }
  
  &:active {
    background: #edf2f7;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: white;
      color: #2d3748;
    }
  }
`;

const MenuIcon = styled.span`
  font-size: 1rem;
`;

interface ContextMenuProps {
  x: number;
  y: number;
  items: Array<{
    label: string;
    icon?: string;
    onClick: () => void;
    disabled?: boolean;
  }>;
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off screen
  const [adjustedX, adjustedY] = React.useMemo(() => {
    let newX = x;
    let newY = y;
    
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      if (x + rect.width > window.innerWidth) {
        newX = window.innerWidth - rect.width - 10;
      }
      if (y + rect.height > window.innerHeight) {
        newY = window.innerHeight - rect.height - 10;
      }
    }
    
    return [newX, newY];
  }, [x, y]);

  return (
    <>
      <MenuOverlay />
      <MenuContainer ref={menuRef} x={adjustedX} y={adjustedY}>
        {items.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              if (!item.disabled) {
                item.onClick();
                onClose();
              }
            }}
            disabled={item.disabled}
          >
            {item.icon && <MenuIcon>{item.icon}</MenuIcon>}
            {item.label}
          </MenuItem>
        ))}
      </MenuContainer>
    </>
  );
};

export default ContextMenu;

